import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Lock } from "lucide-react";

export const STATUS_LABEL = {
  AVAILABLE: "AVAILABLE NOW",
  COMING_SOON: "COMING SOON",
  WAITLIST: "APPLICATION ONLY",
  ENROLLMENT_OPEN: "ENROLLMENT OPEN",
  ENROLLMENT_CLOSED: "ENROLLMENT CLOSED",
};

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`mono text-[11px] tracking-[0.35em] text-primary ${className}`}>{children}</div>
);

export const OfferCard = ({ product }) => {
  const navigate = useNavigate();
  const status = product.effective_status || product.status;
  const closed = status === "ENROLLMENT_CLOSED";
  const coming = status === "COMING_SOON";
  const isCheckout = product.cta_destination === "checkout";
  const ctaText = closed && isCheckout ? "JOIN THE WAITLIST" : product.cta_text;

  const go = () => {
    if (isCheckout) {
      navigate(closed ? `/waitlist/${product.key}` : `/buy/${product.lookup_key}`);
      return;
    }
    navigate(product.cta_destination);
  };

  return (
    <div
      data-testid={`offer-${product.key}`}
      className={`relative flex flex-col border bg-[#141414] p-6 transition-all hover:-translate-y-1 md:p-8 ${
        product.featured ? "border-primary" : "border-border hover:border-primary/60"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="mono text-[10px] tracking-[0.3em] text-muted-foreground">
          LEVEL {product.level}
        </span>
        <span
          data-testid={`offer-status-${product.key}`}
          className={`mono border px-2 py-0.5 text-[9px] tracking-widest ${
            coming || closed
              ? "border-border text-muted-foreground"
              : "border-primary/60 text-primary"
          }`}
        >
          {STATUS_LABEL[status] || status}
        </span>
      </div>

      <h3 className="display mt-4 text-3xl uppercase leading-none md:text-4xl">{product.name}</h3>
      <div className="mt-2 flex items-baseline gap-3">
        <span className="display text-3xl text-primary">
          {product.sale_price ? `$${product.sale_price}` : product.price_label}
        </span>
        {product.sale_price && product.price ? (
          <span className="mono text-sm text-muted-foreground line-through">${product.price}</span>
        ) : null}
      </div>
      <p className="mt-4 text-base text-white">{product.headline}</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{product.description}</p>

      <ul className="mt-5 space-y-1.5">
        {(product.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
            <Check size={14} className="mt-0.5 shrink-0 text-primary" />
            {f}
          </li>
        ))}
      </ul>

      {product.note ? (
        <p className="mono mt-5 border-l-2 border-primary/60 pl-3 text-[11px] leading-relaxed text-primary">
          {product.note}
        </p>
      ) : null}

      <button
        data-testid={`offer-cta-${product.key}`}
        onClick={go}
        className="display group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-5 py-3 text-lg uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
      >
        {ctaText}
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
      </button>
    </div>
  );
};

export const OpportunityCard = ({ product }) => (
  <div
    data-testid={`opportunity-${product.key}`}
    className="flex flex-col border border-border bg-[#0d0d0d] p-6 transition-all hover:border-primary/60 md:p-8"
  >
    <Eyebrow>{product.price_label}</Eyebrow>
    <h3 className="display mt-3 text-3xl uppercase leading-none md:text-4xl">{product.name}</h3>
    <p className="mt-3 text-base text-white">{product.headline}</p>
    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{product.description}</p>
    <div className="mono mt-5 text-[10px] tracking-widest text-muted-foreground">
      POTENTIAL PACKAGE
    </div>
    <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
      {(product.features || []).map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
          <Check size={14} className="mt-0.5 shrink-0 text-primary" />
          {f}
        </li>
      ))}
    </ul>
    <Link
      to={product.cta_destination}
      data-testid={`opportunity-cta-${product.key}`}
      className="display mt-7 inline-flex w-full items-center justify-center gap-2 rounded-sm border border-primary px-5 py-3 text-lg uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
    >
      {product.cta_text}
    </Link>
    <p className="mono mt-4 flex items-start gap-2 text-[10px] leading-relaxed text-muted-foreground">
      <Lock size={11} className="mt-0.5 shrink-0" />
      Application and qualification required. No instant checkout.
    </p>
  </div>
);

const RUNGS = [
  ["FREE GUIDE", "/guide"],
  ["$9 EVENT CALCULATOR", "#ladder"],
  ["$67 SMASH ACADEMY", "#academy"],
  ["SMASH OPERATOR", "#further"],
];

export const LadderVisual = () => (
  <div data-testid="ladder-visual" className="mx-auto max-w-2xl">
    {RUNGS.map(([label, href], i) => (
      <div key={label}>
        <a
          href={href}
          className="display block border border-border bg-[#141414] px-5 py-5 text-center text-2xl uppercase transition-all hover:-translate-y-0.5 hover:border-primary hover:text-primary md:text-3xl"
          style={{ opacity: 0.55 + i * 0.11 }}
        >
          {label}
        </a>
        {i < RUNGS.length - 1 && (
          <div className="mx-auto h-8 w-px bg-primary/60" aria-hidden="true" />
        )}
      </div>
    ))}
  </div>
);

export const CompareTable = ({ products }) => (
  <div data-testid="compare-table" className="divide-y divide-border border-y border-border">
    {products.map((p) => (
      <div key={p.key} className="grid gap-2 py-5 md:grid-cols-[1fr_2fr] md:gap-8">
        <div className="display text-2xl uppercase text-primary">{p.name}</div>
        <div className="text-sm text-muted-foreground">
          <span className="mono text-[10px] tracking-widest text-white">BEST FOR: </span>
          {p.best_for}
        </div>
      </div>
    ))}
  </div>
);
